import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { TextField } from '@/shared/forms/TextField';
import { Button } from '@/shared/ui/Button';

import { useResendOtp } from '../hooks/useResendOtp';
import { useVerifyOtp } from '../hooks/useVerifyOtp';
import { otpSchema } from '../validation/otp.schema';

const RESEND_COOLDOWN = 30;

/**
 * Step 2 of signup: enter the 6-digit code emailed to `email`. On success the
 * customer is signed in and `onVerified` fires.
 *
 * @param {{ email: string, onVerified: () => void }} props
 */
export function OtpForm({ email, onVerified }) {
  const verify = useVerifyOtp();
  const resend = useResendOtp();
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN);

  const form = useForm({
    resolver: zodResolver(otpSchema),
    defaultValues: { otp: '' },
    mode: 'onSubmit',
  });

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setInterval(() => setCooldown((c) => c - 1), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const onSubmit = form.handleSubmit(async ({ otp }) => {
    try {
      await verify.mutateAsync({ email, otp });
      onVerified();
    } catch (error) {
      form.setError('otp', {
        message:
          error.status === 422 || error.code === 'INVALID_OTP'
            ? 'That code is invalid or has expired.'
            : error.message || 'Unable to verify the code. Please try again.',
      });
    }
  });

  const onResend = async () => {
    try {
      await resend.mutateAsync({ email });
      setCooldown(RESEND_COOLDOWN);
    } catch {
      // resend is intentionally non-enumerating; surface a soft error only
      setCooldown(RESEND_COOLDOWN);
    }
  };

  return (
    <form onSubmit={onSubmit} noValidate className="grid gap-5">
      <p className="text-sm text-[var(--app-muted)]">
        We sent a 6-digit code to <span className="font-semibold text-[var(--app-fg)]">{email}</span>
        . Enter it below to activate your account.
      </p>

      <TextField
        label="Verification code"
        required
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        placeholder="123456"
        error={form.formState.errors.otp?.message}
        {...form.register('otp')}
      />

      <Button type="submit" loading={verify.isPending} className="w-full">
        Verify & continue
      </Button>

      <div className="flex items-center justify-between text-sm">
        <span className="text-[var(--app-muted)]">Didn&apos;t get the code?</span>
        <button
          type="button"
          onClick={onResend}
          disabled={cooldown > 0 || resend.isPending}
          className="font-semibold text-brand-600 transition hover:text-brand-700 disabled:cursor-not-allowed disabled:text-[var(--app-muted)] dark:text-brand-300"
        >
          {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
        </button>
      </div>

      {resend.isSuccess && cooldown === RESEND_COOLDOWN && (
        <p className="text-sm text-emerald-600 dark:text-emerald-400">
          If the account exists, a new code is on its way.
        </p>
      )}
    </form>
  );
}
